/**
 * components/login-register/login-register.tsx
 *
 * Login/register UI component.
 * Initiates PKCE login flow via initiateLogin() — no MSAL dependency.
 * The actual token exchange happens server-side via Azure Function.
 */

'use client';

import React, { useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import { initiateLogin } from '@/helpers/api/user-profile-api';
import { useGetLibraryConstantsDictionaryData } from '@/providers/ConfigProvider';
import LinkButton from '../ui/link-button/link-button';
import Button from '../ui/button/button';
import LivliFamilyLogo from '@/assets/icons/livli-family-logo.svg';

interface LoginRegisterProps {
  node_locale: string;
  brandId: string;
  current_path?: string;
  config: any;
  handleClose?: () => void;
}

const LoginRegister: React.FC<LoginRegisterProps> = ({
  node_locale,
  brandId,
  config,
  current_path,
  handleClose,
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const dictionary = useGetLibraryConstantsDictionaryData();
  const isLivliFamily = config?.NEXT_PUBLIC_IS_MP_LIVLI_FAMILY === 'true';
  const [isLoading, setIsLoading] = useState(false);

  const getCurrentPath = (): string => {
    const params = searchParams.toString();
    const newUrl = params ? `${pathname}?${params}` : pathname;
    return newUrl ?? '/';
  };

  const handleLogin = async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      if (current_path) localStorage.setItem('current_path', current_path);
      /**
       * initiateLogin:
       * 1. Generates PKCE code_verifier + code_challenge
       * 2. Stores verifier in sessionStorage
       * 3. Redirects browser to B2C /authorize with code_challenge
       *
       * After user authenticates, B2C redirects back with ?code=xxx
       * which is handled by the auth callback useEffect in the layout.
       */
      await initiateLogin(node_locale, brandId, config, getCurrentPath());
    } catch (error) {
      console.error('[LoginRegister] Login initiation failed:', error);
      setIsLoading(false);
    }
    // Note: setIsLoading(false) not called on success — page redirects away
  };

  return (
    <div className="max-w-md mx-auto my-12 flex flex-col justify-between h-full">
      <div className="h-full w-full">
        {isLivliFamily && (
          <div className="mb-6">
            <LivliFamilyLogo className="mx-auto" />{' '}
          </div>
        )}
        <h2 className="text-headingm font-title lg:text-headingl mb-6 text-text-default text-center">
          {isLivliFamily
            ? dictionary?.livliFamilySignInHeading
            : dictionary?.siezeThePerks}
        </h2>
        {!isLivliFamily && (
          <p className="text-bodyl text-text-default mb-6 text-center">
            {dictionary?.joinOrSignIn}
          </p>
        )}
      </div>
      <div className="flex-1" />
      <div>
        <Button
          variant="primary"
          className="w-full text-center"
          onClick={handleLogin}
          disabled={isLoading}
        >
          {dictionary?.joinOrSignInButtonText}
        </Button>

        {isLivliFamily && (
          <Button
            variant="secondary"
            className="mt-4 w-full text-center"
            onClick={handleClose}
            disabled={isLoading}
          >
            {dictionary?.cancelText}
          </Button>
        )}
      </div>
    </div>
  );
};

export default LoginRegister;
